import {
  ImageMetadata,
  ExerciseDefinition,
  ResultPayload,
  ImageSet,
  Superset,
  ParticipantExposure,
  AgeBand,
  UserImageExposure,
  HierarchyNode,
  OrganizationHierarchy,
  RoleDefinition,
  ExerciseAssignment,
  WeightedSelection,
  EfficacyMetric,
} from './types'
// Safe forEach wrapper with detailed logging
function safeForEach<T>(
  label: string,
  arr: T[] | undefined | null,
  fn: (item: T, index: number, array: T[]) => void,
): void {
  if (!Array.isArray(arr)) {
    console.error('====================================')
    console.error('[mockData] safeForEach →', label, 'is', arr)
    console.error('[mockData] Type:', typeof arr)
    console.error('[mockData] Stack trace:')
    console.error(new Error().stack)
    console.error('====================================')
    return
  }
  arr.forEach(fn)
}
export const MOCK_IMAGES: ImageMetadata[] = [
  // Diverse representation - African descent
  {
    id: 'img-001',
    filename: 'abstract-001.jpg',
    url: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=200',
    locale: 'EN',
    tags: ['positive', 'smile', 'joy', 'confident'],
    license: 'CC-BY',
    domain: 'Self',
    level: 'Foundational',
    audience: 'adults',
    ethnicity: 'African',
    culture: 'Nigerian',
    ageBands: ['Adults', 'Students'],
    locales: ['en', 'uk'],
    emotionalCategory: 'promotion',
  },
  // Asian representation - East Asian
  {
    id: 'img-002',
    filename: 'pattern-002.png',
    url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
    locale: 'EN',
    tags: ['neutral', 'calm', 'thoughtful'],
    license: 'CC-BY',
    domain: 'Spiritual',
    level: 'Experiential',
    audience: 'adults',
    ethnicity: 'East Asian',
    culture: 'Japanese',
    ageBands: ['Adults', 'Students'],
    locales: ['en', 'uk'],
    emotionalCategory: 'promotion',
  },
  // European representation - Eastern European
  {
    id: 'img-003',
    filename: 'geometric-003.jpg',
    url: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=200',
    locale: 'UK',
    tags: ['negative', 'down', 'worried', 'child'],
    license: 'CC-BY',
    domain: 'Self',
    level: 'Foundational',
    audience: 'kids',
    ethnicity: 'Eastern European',
    culture: 'Ukrainian',
    ageBands: ['5-9', '10-14'],
    locales: ['uk'],
    emotionalCategory: 'prevention',
  },
  // Latin American representation
  {
    id: 'img-004',
    filename: 'visual-004.png',
    url: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=200',
    locale: 'EN',
    tags: ['positive', 'energetic', 'excited'],
    license: 'CC-BY',
    domain: 'Social',
    level: 'Experiential',
    audience: 'kids',
    ethnicity: 'Latin American',
    culture: 'Mexican',
    ageBands: ['10-14', '14-18'],
    locales: ['en', 'uk'],
    emotionalCategory: 'promotion',
  },
  // Middle Eastern representation
  {
    id: 'img-005',
    filename: 'color-005.jpg',
    url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200',
    locale: 'EN',
    tags: ['positive', 'focused', 'determined'],
    license: 'CC-BY',
    domain: 'Material',
    level: 'Aspirational',
    audience: 'corporate',
    ethnicity: 'Middle Eastern',
    culture: 'Lebanese',
    ageBands: ['Adults'],
    locales: ['en', 'uk'],
    emotionalCategory: 'promotion',
  },
  // South Asian representation
  {
    id: 'img-006',
    filename: 'design-006.png',
    url: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200',
    locale: 'EN',
    tags: ['negative', 'anxious', 'stressed'],
    license: 'CC-BY',
    domain: 'Self',
    level: 'Experiential',
    audience: 'adults',
    ethnicity: 'South Asian',
    culture: 'Indian',
    ageBands: ['Students', 'Adults'],
    locales: ['en', 'uk'],
    emotionalCategory: 'prevention',
  },
  // Indigenous representation
  {
    id: 'img-007',
    filename: 'texture-007.jpg',
    url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200',
    locale: 'EN',
    tags: ['positive', 'calm', 'wise', 'peaceful'],
    license: 'CC-BY',
    domain: 'Spiritual',
    level: 'Aspirational',
    audience: 'adults',
    ethnicity: 'Indigenous',
    culture: 'Native American',
    ageBands: ['Adults'],
    locales: ['en'],
    emotionalCategory: 'promotion',
  },
  // Mixed heritage representation
  {
    id: 'img-008',
    filename: 'composition-008.png',
    url: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200',
    locale: 'EN',
    tags: ['positive', 'joy', 'family', 'connection'],
    license: 'CC-BY',
    domain: 'Social',
    level: 'Foundational',
    audience: 'adults',
    ethnicity: 'Mixed',
    culture: 'Multicultural',
    ageBands: ['Adults', 'Students'],
    locales: ['en', 'uk'],
    emotionalCategory: 'promotion',
  },
  // French representation
  {
    id: 'img-009',
    filename: 'form-009.jpg',
    url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
    locale: 'FR',
    tags: ['neutral', 'creative', 'thoughtful'],
    license: 'CC-BY',
    domain: 'Material',
    level: 'Experiential',
    audience: 'adults',
    ethnicity: 'European',
    culture: 'French',
    ageBands: ['Adults'],
    locales: ['fr'],
    emotionalCategory: 'promotion',
  },
  // African diaspora - Caribbean
  {
    id: 'img-010',
    filename: 'shape-010.png',
    url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200',
    locale: 'EN',
    tags: ['positive', 'strong', 'resilient'],
    license: 'CC-BY',
    domain: 'Self',
    level: 'Aspirational',
    audience: 'adults',
    ethnicity: 'African Caribbean',
    culture: 'Jamaican',
    ageBands: ['Adults', 'Students'],
    locales: ['en'],
    emotionalCategory: 'promotion',
  },
  // Southeast Asian representation
  {
    id: 'img-011',
    filename: 'detail-011.jpg',
    url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200',
    locale: 'EN',
    tags: ['positive', 'hopeful', 'optimistic'],
    license: 'CC-BY',
    domain: 'Spiritual',
    level: 'Foundational',
    audience: 'kids',
    ethnicity: 'Southeast Asian',
    culture: 'Vietnamese',
    ageBands: ['10-14', '14-18'],
    locales: ['en', 'uk'],
    emotionalCategory: 'promotion',
  },
  // Pacific Islander representation
  {
    id: 'img-012',
    filename: 'study-012.png',
    url: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=200',
    locale: 'EN',
    tags: ['positive', 'community', 'belonging'],
    license: 'CC-BY',
    domain: 'Social',
    level: 'Aspirational',
    audience: 'adults',
    ethnicity: 'Pacific Islander',
    culture: 'Polynesian',
    ageBands: ['Adults'],
    locales: ['en'],
    emotionalCategory: 'promotion',
  },
]
export const MOCK_IMAGE_SETS: ImageSet[] = [
  {
    id: 'emotional-faces-set-a',
    name: 'Emotional Faces Set A',
    description:
      'Primary emotional assessment set featuring diverse facial expressions across all framework cells. Optimized for adults and students.',
    images: ['img-001', 'img-002', 'img-003', 'img-004', 'img-005', 'img-006'],
    grid: {
      Self: {
        Foundational: ['img-001', 'img-003'],
        Experiential: ['img-006'],
        Aspirational: ['img-010'],
      },
      Material: {
        Foundational: [],
        Experiential: ['img-009'],
        Aspirational: ['img-005'],
      },
      Social: {
        Foundational: ['img-008'],
        Experiential: ['img-004'],
        Aspirational: ['img-012'],
      },
      Spiritual: {
        Foundational: ['img-011'],
        Experiential: ['img-002'],
        Aspirational: ['img-007'],
      },
    },
    ageBands: ['Adults', 'Students'],
    locales: ['en', 'uk'],
    status: 'published',
    version: 1,
    createdBy: 'ab_root',
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2024-09-01T10:00:00Z',
    noRepeatPolicy: {
      enabled: true,
      windowDays: 30,
    },
  },
  {
    id: 'emotional-faces-set-b',
    name: 'Emotional Faces Set B',
    description:
      'Secondary emotional assessment set with complementary expressions. Designed to pair with Set A for comprehensive evaluations.',
    images: ['img-007', 'img-008', 'img-009', 'img-010', 'img-011', 'img-012'],
    grid: {
      Self: {
        Foundational: ['img-001'],
        Experiential: ['img-006'],
        Aspirational: ['img-010'],
      },
      Material: {
        Foundational: [],
        Experiential: ['img-009'],
        Aspirational: ['img-005'],
      },
      Social: {
        Foundational: ['img-008'],
        Experiential: ['img-004'],
        Aspirational: ['img-012'],
      },
      Spiritual: {
        Foundational: ['img-011'],
        Experiential: ['img-002'],
        Aspirational: ['img-007'],
      },
    },
    ageBands: ['Adults', 'Students'],
    locales: ['en', 'uk'],
    status: 'published',
    version: 1,
    createdBy: 'ab_root',
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2024-09-01T10:00:00Z',
    noRepeatPolicy: {
      enabled: false,
    },
  },
]
export const MOCK_SUPERSETS: Superset[] = [
  {
    id: 'superset-adults-en',
    name: 'Adult Emotional Assessment - English',
    description:
      'Comprehensive superset combining Sets A and B for adult populations. Implements no-repeat policy with 3-session window.',
    setIds: ['emotional-faces-set-a', 'emotional-faces-set-b'],
    targetFramework: {
      domains: ['Self', 'Material', 'Social', 'Spiritual'],
      levels: ['Foundational', 'Experiential', 'Aspirational'],
    },
    policies: {
      avoidRepeatsPerParticipant: true,
      repeatWindowSessions: 3,
      perSessionQuota: {
        Self: { Foundational: 3, Experiential: 3, Aspirational: 3 },
        Material: { Foundational: 3, Experiential: 3, Aspirational: 3 },
        Social: { Foundational: 3, Experiential: 3, Aspirational: 3 },
        Spiritual: { Foundational: 3, Experiential: 3, Aspirational: 3 },
      },
      fillStrategy: 'best-effort',
    },
    ageBands: ['Adults', 'Students'],
    locales: ['en', 'uk'],
    status: 'published',
    version: 1,
    createdBy: 'ab_root',
    createdAt: '2024-12-01T10:00:00Z',
    updatedAt: '2024-12-01T10:00:00Z',
  },
]
export const MOCK_PARTICIPANT_EXPOSURE: ParticipantExposure[] = [
  {
    participantId: 'EXT-UKR-000101',
    imageId: 'img-001',
    exerciseId: 'EX-EMO-UK-2025-10-v1',
    firstSeenAt: '2024-09-05T10:15:20Z',
    lastSeenAt: '2024-12-10T14:20:33Z',
    seenCount: 4,
  },
  {
    participantId: 'EXT-UKR-000101',
    imageId: 'img-002',
    exerciseId: 'EX-EMO-UK-2025-10-v1',
    firstSeenAt: '2024-09-05T10:15:20Z',
    lastSeenAt: '2024-11-20T11:30:16Z',
    seenCount: 3,
  },
]
export const MOCK_EXERCISE: ExerciseDefinition = {
  exerciseId: 'EX-EMO-UK-2025-10-v1',
  title: {
    en: 'Emotional State Assessment',
    uk: 'Оцінка емоційного стану',
  },
  sets: {
    A: 'emotional-faces-set-a',
    B: 'emotional-faces-set-b',
  },
  useSuperset: false,
  timing: {
    perImageMs: 1800,
    interStimulusMs: 500,
    maxSetSeconds: 180,
  },
  primingCopy: {
    A: {
      en: 'When I think about what is most important to me, I wish I could feel a little more...',
      uk: 'Коли я думаю про те, що для мене найважливіше, я хотів би відчувати трохи більше...',
    },
    B: {
      en: 'When I think about what is most important to me, I wish I could feel a little less...',
      uk: 'Коли я думаю про те, що для мене найважливіше, я хотів би відчувати трохи менше...',
    },
  },
  algorithmVersion: 'ALG-1.2.0',
  interpretationTemplate: 'TPL-UK-1.0.0',
  locales: ['en', 'uk'],
}
// Ukrainian participant names - Expanded diversity
export const UKRAINIAN_FIRST_NAMES = [
  'Олександр',
  'Марія',
  'Іван',
  'Анна',
  'Дмитро',
  'Олена',
  'Андрій',
  'Катерина',
  'Максим',
  'Софія',
  'Михайло',
  'Дарія',
  'Богдан',
  'Вікторія',
  'Артем',
  'Юлія',
  'Владислав',
  'Аліна',
  'Назар',
  'Поліна',
  'Тарас',
  'Єлизавета',
  'Ярослав',
  'Анастасія',
  'Віталій',
  'Ірина',
  'Сергій',
  'Наталія',
  'Олег',
  'Людмила',
  'Павло',
  'Тетяна',
  'Роман',
  'Оксана',
  'Денис',
  'Галина',
  'Ігор',
  'Світлана',
  'Юрій',
  'Валентина',
  'Василь',
  'Ганна',
  'Петро',
  'Ольга',
  'Степан',
  'Лариса',
  'Володимир',
  'Надія',
]
export const UKRAINIAN_LAST_NAMES = [
  'Коваленко',
  'Шевченко',
  'Бойко',
  'Ткаченко',
  'Кравченко',
  'Мельник',
  'Петренко',
  'Іваненко',
  'Морозов',
  'Павленко',
  'Гриценко',
  'Савченко',
  'Литвиненко',
  'Захарченко',
  'Сидоренко',
  'Бондаренко',
  'Романенко',
  'Василенко',
  'Поліщук',
  'Марченко',
  'Семенов',
  'Козлов',
  'Федоренко',
  'Левченко',
  'Гончаренко',
  'Соколов',
  'Тимошенко',
  'Олійник',
]
export const UKRAINIAN_CITIES = [
  'Київ',
  'Львів',
  'Одеса',
  'Харків',
  'Дніпро',
  'Запоріжжя',
  'Вінниця',
  'Полтава',
  'Чернігів',
  'Черкаси',
  'Житомир',
  'Суми',
  'Рівне',
  'Хмельницький',
  'Івано-Франківськ',
]
// American participant names - Expanded for college students
export const AMERICAN_FIRST_NAMES = [
  'James',
  'Emma',
  'Michael',
  'Olivia',
  'William',
  'Sophia',
  'David',
  'Isabella',
  'Christopher',
  'Mia',
  'Matthew',
  'Charlotte',
  'Daniel',
  'Amelia',
  'Joshua',
  'Harper',
  'Andrew',
  'Evelyn',
  'Ryan',
  'Abigail',
  'Nathan',
  'Emily',
  'Tyler',
  'Madison',
  'Alexander',
  'Elizabeth',
  'Benjamin',
  'Ava',
  'Samuel',
  'Grace',
  'Jacob',
  'Chloe',
  'Ethan',
  'Victoria',
  'Nicholas',
  'Lily',
  'Jonathan',
  'Hannah',
  'Brandon',
  'Zoe',
  'Dylan',
  'Natalie',
  'Christian',
  'Addison',
  'Austin',
  'Lillian',
  'Kevin',
  'Aubrey',
]
export const AMERICAN_LAST_NAMES = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Thompson',
  'White',
  'Harris',
  'Clark',
  'Lewis',
  'Walker',
  'Hall',
  'Allen',
  'Young',
  'King',
  'Wright',
  'Scott',
]
// Update Ukrainian schools with city/states
export const generateUkrainianSchools = () => {
  const schools = []
  const schoolNames = [
    'Київська гімназія № 23',
    'Львівський ліцей № 12',
    'Одеська школа № 45',
    'Харківська гімназія № 8',
    'Дніпровський ліцей № 31',
    'Запорізька школа № 17',
    'Вінницька гімназія № 5',
    'Полтавський ліцей № 19',
    'Чернігівська школа № 28',
    'Черкаська гімназія № 14',
    'Житомирський ліцей № 22',
    'Сумська школа № 36',
    'Рівненська гімназія № 9',
    'Хмельницький ліцей № 26',
    'Івано-Франківська школа № 33',
    'Київська школа № 67',
    'Львівська гімназія № 41',
    'Одеський ліцей № 52',
    'Харківська школа № 15',
    'Дніпровська гімназія № 29',
    'Запорізький ліцей № 44',
    'Вінницька школа № 11',
    'Полтавська гімназія № 38',
    'Чернігівський ліцей № 20',
    'Черкаська школа № 47',
    'Житомирська гімназія № 13',
    'Сумський ліцей № 25',
    'Рівненська школа № 34',
    'Хмельницька гімназія № 18',
    'Івано-Франківський ліцей № 42',
  ]
  schoolNames.forEach((name, index) => {
    const city = UKRAINIAN_CITIES[index % UKRAINIAN_CITIES.length]
    schools.push({
      id: `school-ukr-${index + 1}`,
      name,
      city,
      state: 'Ukraine',
      orgId: 'org-kmg',
      type: 'school' as const,
      groupCount: 4 + Math.floor(Math.random() * 3),
      participantCount: 150 + Math.floor(Math.random() * 100),
    })
  })
  return schools
}
export const UKRAINIAN_SCHOOLS = generateUkrainianSchools()
// Generate Groups for Ukrainian schools (grade-based)
export const generateUkrainianGroups = () => {
  const groups = []
  const gradeBands = [
    { name: 'Grades K-2 Students', gradeLevel: 'K-2', ageBand: '5-9' },
    { name: 'Grades 3-5 Students', gradeLevel: '3-5', ageBand: '10-14' },
    { name: 'Grades 6-8 Students', gradeLevel: '6-8', ageBand: '10-14' },
    { name: 'Grades 9-12 Students', gradeLevel: '9-12', ageBand: '14-18' },
  ]
  safeForEach(
    'mockData.UKRAINIAN_SCHOOLS',
    UKRAINIAN_SCHOOLS,
    (school, schoolIndex) => {
      gradeBands.forEach((band, bandIndex) => {
        groups.push({
          id: `group-ukr-${schoolIndex}-${bandIndex}`,
          name: band.name,
          orgId: 'org-kmg',
          subOrgId: school.id,
          exerciseType: 'standard' as const,
          imageSet: 'emotional-faces-set-a',
          promptCode: 'standard-emotional-assessment',
          pacing: 'monthly' as const,
          subGroupCount: 2 + Math.floor(Math.random() * 3),
          participantCount: 30 + Math.floor(Math.random() * 25),
          createdAt: '2024-01-01T00:00:00Z',
          metadata: {
            gradeLevel: band.gradeLevel,
            ageBand: band.ageBand,
          },
        })
      })
    },
  )
  return groups
}
export const UKRAINIAN_GROUPS = generateUkrainianGroups()
// Generate Sub-Groups for Ukrainian groups (classrooms)
export const generateUkrainianSubGroups = () => {
  const subGroups = []
  safeForEach(
    'mockData.UKRAINIAN_GROUPS',
    UKRAINIAN_GROUPS,
    (group, groupIndex) => {
      const subGroupCount = group.subGroupCount || 2
      for (let i = 0; i < subGroupCount; i++) {
        subGroups.push({
          id: `subgroup-ukr-${groupIndex}-${i}`,
          name: `Classroom ${String.fromCharCode(65 + i)}`,
          groupId: group.id,
          participantCount: 15 + Math.floor(Math.random() * 15),
          createdAt: '2024-01-01T00:00:00Z',
          metadata: {
            roomNumber: `${Math.floor(Math.random() * 300) + 100}`,
          },
        })
      }
    },
  )
  return subGroups
}
export const UKRAINIAN_SUBGROUPS = generateUkrainianSubGroups()
// Generate Ukrainian cohorts from groups - MUST be defined early
function generateUkrainianCohorts() {
  console.log('[mockData] Generating Ukrainian cohorts...')
  const cohorts = []
  if (!Array.isArray(UKRAINIAN_GROUPS)) {
    console.error(
      '[mockData] ❌ UKRAINIAN_GROUPS is not an array:',
      UKRAINIAN_GROUPS,
    )
    return []
  }
  UKRAINIAN_GROUPS.forEach((group, groupIndex) => {
    const school = UKRAINIAN_SCHOOLS.find((s) => s.id === group.subOrgId)
    if (!school) {
      console.warn(`[mockData] School not found for group ${group.id}`)
      return
    }
    cohorts.push({
      id: `cohort-ukr-${groupIndex}`,
      name: group.name,
      schoolId: school.id,
      schoolName: school.name,
      city: school.city,
      subOrgId: school.id,
      participantCount: group.participantCount,
      orgId: 'org-kmg',
      metadata: group.metadata,
    })
  })
  console.log(`[mockData] ✅ Generated ${cohorts.length} Ukrainian cohorts`)
  return cohorts
}
// Export Ukrainian cohorts immediately after generation
export const UKRAINIAN_COHORTS = generateUkrainianCohorts()
// Validate immediately
if (!Array.isArray(UKRAINIAN_COHORTS)) {
  console.error(
    '[mockData] ❌ CRITICAL: UKRAINIAN_COHORTS is not an array after generation!',
  )
  console.error('[mockData] Type:', typeof UKRAINIAN_COHORTS)
  console.error('[mockData] Value:', UKRAINIAN_COHORTS)
} else {
  console.log('[mockData] ✅ UKRAINIAN_COHORTS validated:', {
    length: UKRAINIAN_COHORTS.length,
    sample: UKRAINIAN_COHORTS[0]?.name || 'No cohorts',
    firstId: UKRAINIAN_COHORTS[0]?.id || 'N/A',
  })
}
// Update Ukrainian participants with detailed context
export const generateUkrainianParticipants = (count: number = 1200) => {
  const participants = []
  for (let i = 0; i < count; i++) {
    const firstName =
      UKRAINIAN_FIRST_NAMES[
        Math.floor(Math.random() * UKRAINIAN_FIRST_NAMES.length)
      ]
    const lastName =
      UKRAINIAN_LAST_NAMES[
        Math.floor(Math.random() * UKRAINIAN_LAST_NAMES.length)
      ]
    const subGroup =
      UKRAINIAN_SUBGROUPS[
        Math.floor(Math.random() * UKRAINIAN_SUBGROUPS.length)
      ]
    const group = UKRAINIAN_GROUPS.find((g) => g.id === subGroup.groupId)
    const school = UKRAINIAN_SCHOOLS.find((s) => s.id === group?.subOrgId)
    const atRisk = Math.random() < 0.2
    participants.push({
      id: `participant-ukr-${String(i + 1).padStart(6, '0')}`,
      externalUserId: `UKR-${String(i + 1).padStart(6, '0')}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase().replace(/[^a-z]/g, '')}.${lastName.toLowerCase()}${i}@school.ua`,
      role: 'student',
      orgId: 'org-kmg',
      subOrgId: school?.id,
      groupId: group?.id || '',
      subGroupId: subGroup.id,
      school: school?.name,
      grade: group?.metadata?.gradeLevel,
      classroom: subGroup.name,
      status: 'active' as const,
      lastActive: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      completedAssessments: Math.floor(Math.random() * 5),
      atRisk,
      createdAt: '2024-01-01T00:00:00Z',
      metadata: {
        school: school?.name,
        city: school?.city,
        grade: group?.metadata?.gradeLevel,
        ageBand: group?.metadata?.ageBand,
      },
    })
  }
  return participants
}
export const UKRAINIAN_PARTICIPANTS = generateUkrainianParticipants(1200)
// U.S. College Organizations - Departments
export const DUQUESNE_DEPARTMENTS = [
  {
    id: 'dept-duq-1',
    name: 'McAnulty College of Liberal Arts',
    city: 'Pittsburgh',
    orgId: 'org-duquesne',
  },
  {
    id: 'dept-duq-2',
    name: 'School of Nursing',
    city: 'Pittsburgh',
    orgId: 'org-duquesne',
  },
  {
    id: 'dept-duq-3',
    name: 'Palumbo-Donahue School of Business',
    city: 'Pittsburgh',
    orgId: 'org-duquesne',
  },
  {
    id: 'dept-duq-4',
    name: 'School of Education',
    city: 'Pittsburgh',
    orgId: 'org-duquesne',
  },
  {
    id: 'dept-duq-5',
    name: 'School of Pharmacy',
    city: 'Pittsburgh',
    orgId: 'org-duquesne',
  },
  {
    id: 'dept-duq-6',
    name: 'Rangos School of Health Sciences',
    city: 'Pittsburgh',
    orgId: 'org-duquesne',
  },
]
export const UVM_DEPARTMENTS = [
  {
    id: 'dept-uvm-1',
    name: 'College of Arts & Sciences',
    city: 'Burlington',
    orgId: 'org-uvm',
  },
  {
    id: 'dept-uvm-2',
    name: 'Grossman School of Business',
    city: 'Burlington',
    orgId: 'org-uvm',
  },
  {
    id: 'dept-uvm-3',
    name: 'College of Engineering & Mathematical Sciences',
    city: 'Burlington',
    orgId: 'org-uvm',
  },
  {
    id: 'dept-uvm-4',
    name: 'College of Education & Social Services',
    city: 'Burlington',
    orgId: 'org-uvm',
  },
  {
    id: 'dept-uvm-5',
    name: 'Larner College of Medicine',
    city: 'Burlington',
    orgId: 'org-uvm',
  },
  {
    id: 'dept-uvm-6',
    name: 'College of Agriculture & Life Sciences',
    city: 'Burlington',
    orgId: 'org-uvm',
  },
]
export const MVCC_DEPARTMENTS = [
  {
    id: 'dept-mvcc-1',
    name: 'School of Liberal Arts',
    city: 'Middletown',
    orgId: 'org-mvcc',
  },
  {
    id: 'dept-mvcc-2',
    name: 'School of Business',
    city: 'Middletown',
    orgId: 'org-mvcc',
  },
  {
    id: 'dept-mvcc-3',
    name: 'School of STEM',
    city: 'Middletown',
    orgId: 'org-mvcc',
  },
  {
    id: 'dept-mvcc-4',
    name: 'School of Health Sciences',
    city: 'Middletown',
    orgId: 'org-mvcc',
  },
]
export const SUNY_ORANGE_DEPARTMENTS = [
  {
    id: 'dept-so-1',
    name: 'School of Arts & Humanities',
    city: 'Middletown',
    orgId: 'org-suny-orange',
  },
  {
    id: 'dept-so-2',
    name: 'School of Business & Entrepreneurship',
    city: 'Middletown',
    orgId: 'org-suny-orange',
  },
  {
    id: 'dept-so-3',
    name: 'School of Science, Technology & Engineering',
    city: 'Middletown',
    orgId: 'org-suny-orange',
  },
  {
    id: 'dept-so-4',
    name: 'School of Health Sciences',
    city: 'Middletown',
    orgId: 'org-suny-orange',
  },
]
export const SUNY_WESTCHESTER_DEPARTMENTS = [
  {
    id: 'dept-sw-1',
    name: 'Division of Liberal Arts & Sciences',
    city: 'Valhalla',
    orgId: 'org-suny-westchester',
  },
  {
    id: 'dept-sw-2',
    name: 'Division of Business & Professional Studies',
    city: 'Valhalla',
    orgId: 'org-suny-westchester',
  },
  {
    id: 'dept-sw-3',
    name: 'Division of Health Sciences & Nursing',
    city: 'Valhalla',
    orgId: 'org-suny-westchester',
  },
  {
    id: 'dept-sw-4',
    name: 'Division of Communication Arts',
    city: 'Valhalla',
    orgId: 'org-suny-westchester',
  },
]
export const FINGERLAKES_DEPARTMENTS = [
  {
    id: 'dept-fl-1',
    name: 'School of Liberal Arts & Sciences',
    city: 'Canandaigua',
    orgId: 'org-fingerlakes',
  },
  {
    id: 'dept-fl-2',
    name: 'School of Business & Public Service',
    city: 'Canandaigua',
    orgId: 'org-fingerlakes',
  },
  {
    id: 'dept-fl-3',
    name: 'School of Health Professions',
    city: 'Canandaigua',
    orgId: 'org-fingerlakes',
  },
  {
    id: 'dept-fl-4',
    name: 'School of Nursing & Allied Health',
    city: 'Canandaigua',
    orgId: 'org-fingerlakes',
  },
]
// Update Let Grow schools with city/state
export const LETGROW_SCHOOLS = [
  {
    id: 'school-lg-1',
    name: 'Riverside Elementary School',
    city: 'Portland',
    state: 'OR',
    orgId: 'org-letgrow',
    type: 'school' as const,
    groupCount: 4,
    participantCount: 180,
  },
  {
    id: 'school-lg-2',
    name: 'Oakwood Middle School',
    city: 'Seattle',
    state: 'WA',
    orgId: 'org-letgrow',
    type: 'school' as const,
    groupCount: 4,
    participantCount: 220,
  },
  {
    id: 'school-lg-3',
    name: 'Lincoln High School',
    city: 'Denver',
    state: 'CO',
    orgId: 'org-letgrow',
    type: 'school' as const,
    groupCount: 4,
    participantCount: 340,
  },
  {
    id: 'school-lg-4',
    name: 'Meadowbrook Academy',
    city: 'Austin',
    state: 'TX',
    orgId: 'org-letgrow',
    type: 'school' as const,
    groupCount: 4,
    participantCount: 280,
  },
  {
    id: 'school-lg-5',
    name: 'Summit View Elementary',
    city: 'Phoenix',
    state: 'AZ',
    orgId: 'org-letgrow',
    type: 'school' as const,
    groupCount: 4,
    participantCount: 190,
  },
  {
    id: 'school-lg-6',
    name: 'Westfield Middle School',
    city: 'San Diego',
    state: 'CA',
    orgId: 'org-letgrow',
    type: 'school' as const,
    groupCount: 4,
    participantCount: 250,
  },
]
// Generate Let Grow Groups (grade-based with parents/teachers)
export const generateLetGrowGroups = () => {
  const groups = []
  safeForEach(
    'mockData.LETGROW_SCHOOLS',
    LETGROW_SCHOOLS,
    (school, schoolIndex) => {
      const gradeBands = [
        { name: 'Grades K-2 Students', gradeLevel: 'K-2', ageBand: '5-9' },
        { name: 'Grades 3-5 Students', gradeLevel: '3-5', ageBand: '10-14' },
        {
          name: 'K-2 Parents & Teachers',
          gradeLevel: 'K-2',
          ageBand: 'Adults',
        },
        {
          name: '3-5 Parents & Teachers',
          gradeLevel: '3-5',
          ageBand: 'Adults',
        },
      ]
      gradeBands.forEach((band, bandIndex) => {
        groups.push({
          id: `group-lg-${schoolIndex}-${bandIndex}`,
          name: band.name,
          orgId: 'org-letgrow',
          subOrgId: school.id,
          exerciseType: 'standard' as const,
          imageSet: 'emotional-faces-set-a',
          promptCode: band.name.includes('Parents')
            ? 'parent-resilience-assessment'
            : 'student-resilience-assessment',
          pacing: 'monthly' as const,
          subGroupCount: band.name.includes('Parents') ? 0 : 2,
          participantCount: band.name.includes('Parents')
            ? 25 + Math.floor(Math.random() * 15)
            : 40 + Math.floor(Math.random() * 20),
          createdAt: '2024-01-01T00:00:00Z',
          metadata: {
            gradeLevel: band.gradeLevel,
            ageBand: band.ageBand,
            description: band.name.includes('Parents')
              ? 'Parent and teacher resilience tracking'
              : 'Student resilience assessment',
          },
        })
      })
    },
  )
  return groups
}
export const LETGROW_GROUPS = generateLetGrowGroups()
// Generate Let Grow Sub-Groups (classrooms for students only)
export const generateLetGrowSubGroups = () => {
  const subGroups = []
  safeForEach(
    'mockData.LETGROW_GROUPS',
    LETGROW_GROUPS,
    (group, groupIndex) => {
      if (group.subGroupCount && group.subGroupCount > 0) {
        for (let i = 0; i < group.subGroupCount; i++) {
          subGroups.push({
            id: `subgroup-lg-${groupIndex}-${i}`,
            name: `Room ${Math.floor(Math.random() * 300) + 100}`,
            groupId: group.id,
            participantCount: 18 + Math.floor(Math.random() * 12),
            createdAt: '2024-01-01T00:00:00Z',
            metadata: {
              roomNumber: `${Math.floor(Math.random() * 300) + 100}`,
            },
          })
        }
      }
    },
  )
  return subGroups
}
export const LETGROW_SUBGROUPS = generateLetGrowSubGroups()
// Update Let Grow participants with detailed context
export const generateLetGrowParticipants = (count: number = 900) => {
  const participants = []
  for (let i = 0; i < count; i++) {
    const firstName =
      AMERICAN_FIRST_NAMES[
        Math.floor(Math.random() * AMERICAN_FIRST_NAMES.length)
      ]
    const lastName =
      AMERICAN_LAST_NAMES[
        Math.floor(Math.random() * AMERICAN_LAST_NAMES.length)
      ]
    const group =
      LETGROW_GROUPS[Math.floor(Math.random() * LETGROW_GROUPS.length)]
    const school = LETGROW_SCHOOLS.find((s) => s.id === group.subOrgId)
    // Determine role based on group name
    const isStudent = !group.name.includes('Parents')
    const isTeacher = group.name.includes('Teachers')
    const role = isStudent ? 'student' : isTeacher ? 'teacher' : 'parent'
    const subGroup = isStudent
      ? LETGROW_SUBGROUPS.find((sg) => sg.groupId === group.id)
      : null
    const atRisk = Math.random() < 0.08
    participants.push({
      id: `participant-lg-${String(i + 1).padStart(4, '0')}`,
      externalUserId: `LG-${String(i + 1).padStart(4, '0')}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@letgrow.org`,
      role,
      orgId: 'org-letgrow',
      subOrgId: school?.id,
      groupId: group.id,
      subGroupId: subGroup?.id,
      school: school?.name,
      grade: group.metadata?.gradeLevel,
      classroom: subGroup?.name,
      status: 'active' as const,
      lastActive: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      completedAssessments: Math.floor(Math.random() * 4),
      atRisk,
      createdAt: '2024-01-01T00:00:00Z',
      metadata: {
        school: school?.name,
        city: school?.city,
        grade: group.metadata?.gradeLevel,
        ageBand: group.metadata?.ageBand,
      },
    })
  }
  return participants
}
export const LETGROW_PARTICIPANTS = generateLetGrowParticipants(900)
// Add Dick's Sporting Goods mock data
export const DICKS_STORES = [
  {
    id: 'store-dicks-1',
    name: "Dick's - Pittsburgh Downtown",
    city: 'Pittsburgh',
    state: 'PA',
    orgId: 'org-dicks',
    type: 'location' as const,
    groupCount: 5,
    participantCount: 45,
  },
  {
    id: 'store-dicks-2',
    name: "Dick's - King of Prussia",
    city: 'King of Prussia',
    state: 'PA',
    orgId: 'org-dicks',
    type: 'location' as const,
    groupCount: 6,
    participantCount: 52,
  },
  {
    id: 'store-dicks-3',
    name: "Dick's - Philadelphia Center City",
    city: 'Philadelphia',
    state: 'PA',
    orgId: 'org-dicks',
    type: 'location' as const,
    groupCount: 5,
    participantCount: 48,
  },
]
export const generateDicksGroups = () => {
  const groups = []
  const departments = [
    'Athletic Footwear',
    'Team Sports',
    'Outdoor Recreation',
    'Fitness Equipment',
    'Customer Service',
    'Management',
  ]
  DICKS_STORES.forEach((store, storeIndex) => {
    departments.forEach((dept, deptIndex) => {
      groups.push({
        id: `group-dicks-${storeIndex}-${deptIndex}`,
        name: dept,
        orgId: 'org-dicks',
        subOrgId: store.id,
        exerciseType: 'standard' as const,
        participantCount: 7 + Math.floor(Math.random() * 5),
        createdAt: '2024-01-01T00:00:00Z',
        metadata: {
          department: dept,
        },
      })
    })
  })
  return groups
}
export const DICKS_GROUPS = generateDicksGroups()
export const generateDicksParticipants = (count: number = 150) => {
  const participants = []
  const jobTitles = [
    'Sales Associate',
    'Department Lead',
    'Assistant Manager',
    'Store Manager',
    'Inventory Specialist',
  ]
  for (let i = 0; i < count; i++) {
    const firstName =
      AMERICAN_FIRST_NAMES[
        Math.floor(Math.random() * AMERICAN_FIRST_NAMES.length)
      ]
    const lastName =
      AMERICAN_LAST_NAMES[
        Math.floor(Math.random() * AMERICAN_LAST_NAMES.length)
      ]
    const group = DICKS_GROUPS[Math.floor(Math.random() * DICKS_GROUPS.length)]
    const store = DICKS_STORES.find((s) => s.id === group.subOrgId)
    const jobTitle = jobTitles[Math.floor(Math.random() * jobTitles.length)]
    participants.push({
      id: `participant-dicks-${String(i + 1).padStart(4, '0')}`,
      externalUserId: `DICKS-${String(i + 1).padStart(4, '0')}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@dicks.com`,
      role: 'employee',
      orgId: 'org-dicks',
      subOrgId: store?.id,
      groupId: group.id,
      school: store?.name,
      grade: group.metadata?.department,
      classroom: jobTitle,
      status: 'active' as const,
      lastActive: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      completedAssessments: Math.floor(Math.random() * 3),
      atRisk: Math.random() < 0.15,
      createdAt: '2024-01-01T00:00:00Z',
      metadata: {
        department: group.metadata?.department,
        jobTitle,
        city: store?.city,
      },
    })
  }
  return participants
}
export const DICKS_PARTICIPANTS = generateDicksParticipants(150)
// Add Acme Supplies mock data
export const ACME_DEPARTMENTS = [
  {
    id: 'dept-acme-1',
    name: 'Sales & Marketing',
    city: 'New York',
    orgId: 'org-acme',
    type: 'department' as const,
    groupCount: 3,
    participantCount: 35,
  },
  {
    id: 'dept-acme-2',
    name: 'Operations',
    city: 'New York',
    orgId: 'org-acme',
    type: 'department' as const,
    groupCount: 4,
    participantCount: 42,
  },
  {
    id: 'dept-acme-3',
    name: 'Finance & Accounting',
    city: 'New York',
    orgId: 'org-acme',
    type: 'department' as const,
    groupCount: 2,
    participantCount: 28,
  },
]
export const generateAcmeGroups = () => {
  const groups = []
  const functions = {
    'Sales & Marketing': ['Inside Sales', 'Field Sales', 'Marketing'],
    Operations: [
      'Warehouse',
      'Logistics',
      'Customer Service',
      'Quality Control',
    ],
    'Finance & Accounting': ['Accounts Payable', 'Financial Planning'],
  }
  ACME_DEPARTMENTS.forEach((dept, deptIndex) => {
    const deptFunctions = functions[dept.name as keyof typeof functions] || []
    deptFunctions.forEach((func, funcIndex) => {
      groups.push({
        id: `group-acme-${deptIndex}-${funcIndex}`,
        name: func,
        orgId: 'org-acme',
        subOrgId: dept.id,
        exerciseType: 'standard' as const,
        participantCount: 10 + Math.floor(Math.random() * 8),
        createdAt: '2024-01-01T00:00:00Z',
        metadata: {
          department: dept.name,
          function: func,
        },
      })
    })
  })
  return groups
}
export const ACME_GROUPS = generateAcmeGroups()
export const generateAcmeParticipants = (count: number = 120) => {
  const participants = []
  const roles = [
    'Coordinator',
    'Specialist',
    'Manager',
    'Senior Manager',
    'Director',
  ]
  for (let i = 0; i < count; i++) {
    const firstName =
      AMERICAN_FIRST_NAMES[
        Math.floor(Math.random() * AMERICAN_FIRST_NAMES.length)
      ]
    const lastName =
      AMERICAN_LAST_NAMES[
        Math.floor(Math.random() * AMERICAN_LAST_NAMES.length)
      ]
    const group = ACME_GROUPS[Math.floor(Math.random() * ACME_GROUPS.length)]
    const dept = ACME_DEPARTMENTS.find((d) => d.id === group.subOrgId)
    const role = roles[Math.floor(Math.random() * roles.length)]
    participants.push({
      id: `participant-acme-${String(i + 1).padStart(4, '0')}`,
      externalUserId: `ACME-${String(i + 1).padStart(4, '0')}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@acmesupplies.com`,
      role: 'employee',
      orgId: 'org-acme',
      subOrgId: dept?.id,
      groupId: group.id,
      school: dept?.name,
      grade: group.metadata?.function,
      classroom: role,
      status: 'active' as const,
      lastActive: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      completedAssessments: Math.floor(Math.random() * 3),
      atRisk: Math.random() < 0.12,
      createdAt: '2024-01-01T00:00:00Z',
      metadata: {
        department: dept?.name,
        function: group.metadata?.function,
        jobTitle: role,
      },
    })
  }
  return participants
}
export const ACME_PARTICIPANTS = generateAcmeParticipants(120)
// Update college structures - Groups only (no sub-orgs for MVCC, departments as context for others)
export const generateMVCCGroups = () => {
  const groups = [
    {
      name: "Men's Soccer Team",
      exerciseType: 'unique' as const,
      promptCode: 'team-cohesion-soccer',
    },
    {
      name: "Women's Basketball Team",
      exerciseType: 'unique' as const,
      promptCode: 'team-cohesion-basketball',
    },
    {
      name: 'Student Congress',
      exerciseType: 'unique' as const,
      promptCode: 'leadership-assessment',
    },
    {
      name: 'Psychology 101 - Fall 2024',
      exerciseType: 'standard' as const,
      promptCode: 'student-wellness',
    },
    {
      name: 'First Year Experience - Spring 2025',
      exerciseType: 'standard' as const,
      promptCode: 'student-wellness',
    },
    {
      name: 'Nursing Program - Cohort A',
      exerciseType: 'standard' as const,
      promptCode: 'stress-management',
    },
  ]
  return groups.map((g, index) => ({
    id: `group-mvcc-${index}`,
    name: g.name,
    orgId: 'org-mvcc',
    exerciseType: g.exerciseType,
    imageSet: 'emotional-faces-set-a',
    promptCode: g.promptCode,
    assignedExercise:
      g.exerciseType === 'unique' ? `ex-mvcc-${g.promptCode}` : undefined,
    pacing: 'weekly' as const,
    participantCount: 40 + Math.floor(Math.random() * 35),
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      description: g.name.includes('Team')
        ? 'Athletic team assessment'
        : 'Academic cohort assessment',
    },
  }))
}
export const MVCC_GROUPS = generateMVCCGroups()
// Generate MVCC cohorts (matches the groups structure)
export const generateMVCCCohorts = () => {
  const cohorts = [
    {
      id: 'cohort-mvcc-1',
      name: "Men's Soccer Team",
      schoolId: 'dept-mvcc-1',
      schoolName: 'School of Liberal Arts',
      city: 'Middletown',
      subOrgId: 'dept-mvcc-1',
      participantCount: 45,
      orgId: 'org-mvcc',
    },
    {
      id: 'cohort-mvcc-2',
      name: "Women's Basketball Team",
      schoolId: 'dept-mvcc-1',
      schoolName: 'School of Liberal Arts',
      city: 'Middletown',
      subOrgId: 'dept-mvcc-1',
      participantCount: 42,
      orgId: 'org-mvcc',
    },
    {
      id: 'cohort-mvcc-3',
      name: 'Student Congress',
      schoolId: 'dept-mvcc-2',
      schoolName: 'School of Business',
      city: 'Middletown',
      subOrgId: 'dept-mvcc-2',
      participantCount: 50,
      orgId: 'org-mvcc',
    },
    {
      id: 'cohort-mvcc-4',
      name: 'Psychology 101 - Fall 2024',
      schoolId: 'dept-mvcc-1',
      schoolName: 'School of Liberal Arts',
      city: 'Middletown',
      subOrgId: 'dept-mvcc-1',
      participantCount: 65,
      orgId: 'org-mvcc',
    },
    {
      id: 'cohort-mvcc-5',
      name: 'First Year Experience - Spring 2025',
      schoolId: 'dept-mvcc-1',
      schoolName: 'School of Liberal Arts',
      city: 'Middletown',
      subOrgId: 'dept-mvcc-1',
      participantCount: 72,
      orgId: 'org-mvcc',
    },
    {
      id: 'cohort-mvcc-6',
      name: 'Nursing Program - Cohort A',
      schoolId: 'dept-mvcc-4',
      schoolName: 'School of Health Sciences',
      city: 'Middletown',
      subOrgId: 'dept-mvcc-4',
      participantCount: 58,
      orgId: 'org-mvcc',
    },
  ]
  return cohorts
}
export const MVCC_COHORTS = generateMVCCCohorts()
// Generate MVCC participants (directly under groups, no sub-groups)
export const generateMVCCParticipants = (count: number = 520) => {
  const participants = []
  for (let i = 0; i < count; i++) {
    const firstName =
      AMERICAN_FIRST_NAMES[
        Math.floor(Math.random() * AMERICAN_FIRST_NAMES.length)
      ]
    const lastName =
      AMERICAN_LAST_NAMES[
        Math.floor(Math.random() * AMERICAN_LAST_NAMES.length)
      ]
    const group = MVCC_GROUPS[Math.floor(Math.random() * MVCC_GROUPS.length)]
    const atRisk = Math.random() < 0.2
    participants.push({
      id: `participant-mvcc-${String(i + 1).padStart(4, '0')}`,
      externalUserId: `MVCC-${String(i + 1).padStart(4, '0')}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}${i}@mvcc.edu`,
      orgId: 'org-mvcc',
      groupId: group.id,
      status:
        atRisk && Math.random() < 0.1
          ? ('inactive' as const)
          : ('active' as const),
      lastActive: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      completedAssessments: Math.floor(Math.random() * 4),
      atRisk,
      createdAt: '2024-01-01T00:00:00Z',
      metadata: {
        ageBand: 'Students',
      },
    })
  }
  return participants
}
export const MVCC_PARTICIPANTS = generateMVCCParticipants(520)
// Generate cohorts for U.S. colleges (course-based)
export const generateUniversityCohorts = (
  departments: any[],
  orgId: string,
  prefix: string,
) => {
  const cohorts = []
  const courses = [
    'Introduction to Psychology - Fall 2024',
    'Student Wellness Seminar - Spring 2025',
    'First Year Experience - Fall 2024',
    'Mental Health & Wellbeing - Spring 2025',
    'Stress Management - Fall 2024',
    'Personal Development - Spring 2025',
    'Academic Success Strategies - Fall 2024',
    'Health Psychology - Spring 2025',
  ]
  safeForEach('mockData.departments', departments, (dept, deptIndex) => {
    const courseCount = 4 + Math.floor(Math.random() * 3)
    for (let i = 0; i < courseCount; i++) {
      cohorts.push({
        id: `cohort-${prefix}-${deptIndex}-${i}`,
        name: courses[i % courses.length],
        schoolId: dept.id,
        schoolName: dept.name,
        city: dept.city,
        subOrgId: dept.id,
        participantCount: 40 + Math.floor(Math.random() * 35),
        orgId: orgId,
      })
    }
  })
  return cohorts
}
export const DUQUESNE_COHORTS = generateUniversityCohorts(
  DUQUESNE_DEPARTMENTS,
  'org-duquesne',
  'duq',
)
export const UVM_COHORTS = generateUniversityCohorts(
  UVM_DEPARTMENTS,
  'org-uvm',
  'uvm',
)
export const SUNY_ORANGE_COHORTS = generateUniversityCohorts(
  SUNY_ORANGE_DEPARTMENTS,
  'org-suny-orange',
  'so',
)
export const SUNY_WESTCHESTER_COHORTS = generateUniversityCohorts(
  SUNY_WESTCHESTER_DEPARTMENTS,
  'org-suny-westchester',
  'sw',
)
export const FINGERLAKES_COHORTS = generateUniversityCohorts(
  FINGERLAKES_DEPARTMENTS,
  'org-fingerlakes',
  'fl',
)
// Generate U.S. college participants (500+ per org)
export const generateUniversityParticipants = (
  cohorts: any[],
  orgId: string,
  prefix: string,
  count: number,
) => {
  const participants = []
  for (let i = 0; i < count; i++) {
    const firstName =
      AMERICAN_FIRST_NAMES[
        Math.floor(Math.random() * AMERICAN_FIRST_NAMES.length)
      ]
    const lastName =
      AMERICAN_LAST_NAMES[
        Math.floor(Math.random() * AMERICAN_LAST_NAMES.length)
      ]
    const cohort = cohorts[Math.floor(Math.random() * cohorts.length)]
    const atRisk = Math.random() < 0.2 // 20% at-risk
    participants.push({
      id: `participant-${prefix}-${String(i + 1).padStart(4, '0')}`,
      externalUserId: `${prefix.toUpperCase()}-${String(i + 1).padStart(4, '0')}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}${i}@${prefix}.edu`,
      cohortId: cohort.id,
      cohortName: cohort.name,
      schoolId: cohort.schoolId,
      schoolName: cohort.schoolName,
      city: cohort.city,
      status: atRisk && Math.random() < 0.1 ? 'inactive' : 'active',
      lastActive: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      completedAssessments: Math.floor(Math.random() * 4),
      orgId: orgId,
      atRisk,
    })
  }
  return participants
}
export const DUQUESNE_PARTICIPANTS = generateUniversityParticipants(
  DUQUESNE_COHORTS,
  'org-duquesne',
  'duq',
  580,
)
export const UVM_PARTICIPANTS = generateUniversityParticipants(
  UVM_COHORTS,
  'org-uvm',
  'uvm',
  620,
)
export const SUNY_ORANGE_PARTICIPANTS = generateUniversityParticipants(
  SUNY_ORANGE_COHORTS,
  'org-suny-orange',
  'so',
  540,
)
export const SUNY_WESTCHESTER_PARTICIPANTS = generateUniversityParticipants(
  SUNY_WESTCHESTER_COHORTS,
  'org-suny-westchester',
  'sw',
  510,
)
export const FINGERLAKES_PARTICIPANTS = generateUniversityParticipants(
  FINGERLAKES_COHORTS,
  'org-fingerlakes',
  'fl',
  500,
)
// Generate LetGrow cohorts
export const generateLetGrowCohorts = () => {
  const cohorts = []
  const gradeBands = [
    { id: 'k-2', name: 'K-2', grades: ['K', '1', '2'] },
    { id: '3-5', name: '3-5', grades: ['3', '4', '5'] },
    { id: '6-8', name: '6-8', grades: ['6', '7', '8'] },
    { id: '9-12', name: '9-12', grades: ['9', '10', '11', '12'] },
  ]
  safeForEach(
    'mockData.LETGROW_SCHOOLS',
    LETGROW_SCHOOLS,
    (school, schoolIndex) => {
      safeForEach('mockData.gradeBands', gradeBands, (band) => {
        const cohortCount = 2 + Math.floor(Math.random() * 2)
        for (let i = 0; i < cohortCount; i++) {
          cohorts.push({
            id: `cohort-lg-${schoolIndex}-${band.id}-${i}`,
            name: `${band.name} - Section ${String.fromCharCode(65 + i)}`,
            schoolId: school.id,
            schoolName: school.name,
            city: school.city,
            subOrgId: school.id,
            gradeBand: band.name,
            participantCount: 18 + Math.floor(Math.random() * 15),
            orgId: 'org-letgrow',
          })
        }
      })
    },
  )
  return cohorts
}
export const LETGROW_COHORTS = generateLetGrowCohorts()
// Combined American college participants for easy reference
export const AMERICAN_PARTICIPANTS = [
  ...DUQUESNE_PARTICIPANTS,
  ...UVM_PARTICIPANTS,
  ...MVCC_PARTICIPANTS,
  ...SUNY_ORANGE_PARTICIPANTS,
  ...SUNY_WESTCHESTER_PARTICIPANTS,
  ...FINGERLAKES_PARTICIPANTS,
]
// Combined exports - DEFENSIVE: Check all cohort arrays exist
export const MOCK_COHORTS = [
  ...(Array.isArray(DUQUESNE_COHORTS) ? DUQUESNE_COHORTS : []),
  ...(Array.isArray(UVM_COHORTS) ? UVM_COHORTS : []),
  ...(Array.isArray(MVCC_COHORTS) ? MVCC_COHORTS : []),
  ...(Array.isArray(SUNY_ORANGE_COHORTS) ? SUNY_ORANGE_COHORTS : []),
  ...(Array.isArray(SUNY_WESTCHESTER_COHORTS) ? SUNY_WESTCHESTER_COHORTS : []),
  ...(Array.isArray(FINGERLAKES_COHORTS) ? FINGERLAKES_COHORTS : []),
  ...(Array.isArray(LETGROW_COHORTS) ? LETGROW_COHORTS : []),
  ...(Array.isArray(UKRAINIAN_COHORTS) ? UKRAINIAN_COHORTS : []),
]
// Validate MOCK_COHORTS
console.log('[mockData] ✅ MOCK_COHORTS assembled:', {
  total: MOCK_COHORTS.length,
  breakdown: {
    duquesne: DUQUESNE_COHORTS?.length || 0,
    uvm: UVM_COHORTS?.length || 0,
    mvcc: MVCC_COHORTS?.length || 0,
    sunyOrange: SUNY_ORANGE_COHORTS?.length || 0,
    sunyWestchester: SUNY_WESTCHESTER_COHORTS?.length || 0,
    fingerlakes: FINGERLAKES_COHORTS?.length || 0,
    letgrow: LETGROW_COHORTS?.length || 0,
    ukrainian: UKRAINIAN_COHORTS?.length || 0,
  },
})
export const MOCK_SUB_ORGS = [
  ...UKRAINIAN_SCHOOLS,
  ...DUQUESNE_DEPARTMENTS,
  ...UVM_DEPARTMENTS,
  ...MVCC_DEPARTMENTS,
  ...SUNY_ORANGE_DEPARTMENTS,
  ...SUNY_WESTCHESTER_DEPARTMENTS,
  ...FINGERLAKES_DEPARTMENTS,
  ...LETGROW_SCHOOLS,
  ...DICKS_STORES,
  ...ACME_DEPARTMENTS,
]
export const MOCK_SCHOOLS = [...UKRAINIAN_SCHOOLS, ...LETGROW_SCHOOLS]
// Generate Ukrainian results for Kyiv Mental Guard (4,800+ results)
export const generateUkrainianResults = (count: number = 4823) => {
  const results: ResultPayload[] = []
  const participants = UKRAINIAN_PARTICIPANTS.slice(0, count)
  participants.forEach((participant, index) => {
    const atRisk = Math.random() > 0.85 // 15% at risk
    const positiveAffect = atRisk
      ? 30 + Math.random() * 20
      : 55 + Math.random() * 30
    const negativeAffect = atRisk
      ? 60 + Math.random() * 25
      : 25 + Math.random() * 30
    results.push({
      externalUserId: participant.externalUserId,
      exerciseId: 'ex-ukrainian-kids',
      selections: [],
      qualityFlags: {
        focusLoss: Math.random() > 0.9,
        incompleteSetA: false,
        incompleteSetB: false,
        insufficientSelections: false,
      },
      computed: {
        positiveAffect: Math.round(positiveAffect),
        negativeAffect: Math.round(negativeAffect),
        arousal: Math.round(45 + Math.random() * 30),
        confidence: Math.round(70 + Math.random() * 25),
      },
      deliveredToSpaces: {
        orgId: 'KS-ORG-KMG-001',
        chatbotId: 'chatbot-ukr-kids',
        pushedAt: new Date(
          Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      },
      completedAt: new Date(
        Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      school: participant.schoolName,
      class: participant.cohortName,
      atRisk,
    })
  })
  return results
}
export const UKRAINIAN_RESULTS = generateUkrainianResults(4823)
// Generate American results for MVCC
export const generateAmericanResults = (count: number = 342) => {
  const results: ResultPayload[] = []
  const participants = AMERICAN_PARTICIPANTS.slice(0, count)
  participants.forEach((participant) => {
    const atRisk = Math.random() > 0.88
    const positiveAffect = atRisk
      ? 35 + Math.random() * 20
      : 60 + Math.random() * 25
    const negativeAffect = atRisk
      ? 55 + Math.random() * 30
      : 20 + Math.random() * 25
    results.push({
      externalUserId: participant.externalUserId,
      exerciseId: 'ex-mvcc-wellness',
      selections: [],
      qualityFlags: {
        focusLoss: Math.random() > 0.92,
        incompleteSetA: false,
        incompleteSetB: false,
        insufficientSelections: false,
      },
      computed: {
        positiveAffect: Math.round(positiveAffect),
        negativeAffect: Math.round(negativeAffect),
        arousal: Math.round(45 + Math.random() * 30),
        confidence: Math.round(75 + Math.random() * 20),
      },
      deliveredToSpaces: {
        orgId: 'KS-ORG-MVCC-001',
        chatbotId: 'chatbot-mvcc-wellness',
        pushedAt: new Date(
          Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      },
      completedAt: new Date(
        Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      school: participant.schoolName,
      class: participant.cohortName,
      atRisk,
    })
  })
  return results
}
export const AMERICAN_RESULTS = generateAmericanResults(342)
// Generate results for universities with realistic volumes
export const generateUniversityResults = (
  participants: any[],
  orgId: string,
  exerciseId: string,
  count: number,
) => {
  const results: ResultPayload[] = []
  const selectedParticipants = participants.slice(0, count)
  selectedParticipants.forEach((participant) => {
    const atRisk = Math.random() > 0.87
    const positiveAffect = atRisk
      ? 35 + Math.random() * 20
      : 60 + Math.random() * 25
    const negativeAffect = atRisk
      ? 55 + Math.random() * 30
      : 20 + Math.random() * 25
    results.push({
      externalUserId: participant.externalUserId,
      exerciseId: exerciseId,
      selections: [],
      qualityFlags: {
        focusLoss: Math.random() > 0.92,
        incompleteSetA: false,
        incompleteSetB: false,
        insufficientSelections: false,
      },
      computed: {
        positiveAffect: Math.round(positiveAffect),
        negativeAffect: Math.round(negativeAffect),
        arousal: Math.round(45 + Math.random() * 30),
        confidence: Math.round(75 + Math.random() * 20),
      },
      deliveredToSpaces: {
        orgId: orgId,
        chatbotId: `chatbot-${orgId}`,
        pushedAt: new Date(
          Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      },
      completedAt: new Date(
        Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      school: participant.schoolName,
      class: participant.cohortName,
      atRisk,
    })
  })
  return results
}
export const DUQUESNE_RESULTS = generateUniversityResults(
  DUQUESNE_PARTICIPANTS,
  'KS-ORG-DUQ-001',
  'ex-duquesne-wellness',
  428,
)
export const UVM_RESULTS = generateUniversityResults(
  UVM_PARTICIPANTS,
  'KS-ORG-UVM-001',
  'ex-uvm-wellness',
  512,
)
export const SUNY_ORANGE_RESULTS = generateUniversityResults(
  SUNY_ORANGE_PARTICIPANTS,
  'KS-ORG-SUNYO-001',
  'ex-sunyo-wellness',
  325,
)
export const SUNY_WESTCHESTER_RESULTS = generateUniversityResults(
  SUNY_WESTCHESTER_PARTICIPANTS,
  'KS-ORG-SUNYW-001',
  'ex-sunyw-wellness',
  298,
)
export const FINGERLAKES_RESULTS = generateUniversityResults(
  FINGERLAKES_PARTICIPANTS,
  'KS-ORG-FLCC-001',
  'ex-flcc-wellness',
  267,
)
// Generate results for LetGrow with realistic volumes
export const generateLetGrowResults = (count: number = 850) => {
  const results: ResultPayload[] = []
  const participants = LETGROW_PARTICIPANTS.slice(0, count)
  participants.forEach((participant) => {
    const atRisk = Math.random() > 0.92 // 8% at-risk for LetGrow
    const positiveAffect = atRisk
      ? 35 + Math.random() * 20
      : 65 + Math.random() * 25
    const negativeAffect = atRisk
      ? 50 + Math.random() * 30
      : 18 + Math.random() * 22
    results.push({
      externalUserId: participant.externalUserId,
      exerciseId: 'ex-letgrow-resilience',
      selections: [],
      qualityFlags: {
        focusLoss: Math.random() > 0.94,
        incompleteSetA: false,
        incompleteSetB: false,
        insufficientSelections: false,
      },
      computed: {
        positiveAffect: Math.round(positiveAffect),
        negativeAffect: Math.round(negativeAffect),
        arousal: Math.round(45 + Math.random() * 30),
        confidence: Math.round(78 + Math.random() * 18),
      },
      deliveredToSpaces: {
        orgId: 'KS-ORG-LETGROW-001',
        chatbotId: 'chatbot-letgrow-resilience',
        pushedAt: new Date(
          Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      },
      completedAt: new Date(
        Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      school: participant.schoolName,
      class: participant.cohortName,
      atRisk,
    })
  })
  return results
}
export const LETGROW_RESULTS = generateLetGrowResults(850)
// Combined results - update to include all universities
export const MOCK_RESULTS = [
  ...UKRAINIAN_RESULTS,
  ...AMERICAN_RESULTS,
  ...DUQUESNE_RESULTS,
  ...UVM_RESULTS,
  ...SUNY_ORANGE_RESULTS,
  ...SUNY_WESTCHESTER_RESULTS,
  ...FINGERLAKES_RESULTS,
  ...LETGROW_RESULTS,
]
// Mock activity data for dashboard
export const MOCK_ACTIVITY = [
  {
    id: 'activity-1',
    title: 'New assessment completed',
    time: '5 minutes ago',
    icon: 'CheckCircleIcon' as const,
    color: 'green' as const,
  },
  {
    id: 'activity-2',
    title: 'Cohort "Grade 5A" updated',
    time: '1 hour ago',
    icon: 'UsersIcon' as const,
    color: 'blue' as const,
  },
  {
    id: 'activity-3',
    title: 'Exercise published',
    time: '3 hours ago',
    icon: 'SendIcon' as const,
    color: 'purple' as const,
  },
  {
    id: 'activity-4',
    title: 'At-risk participant flagged',
    time: '5 hours ago',
    icon: 'AlertTriangleIcon' as const,
    color: 'red' as const,
  },
  {
    id: 'activity-5',
    title: 'New cohort created',
    time: '1 day ago',
    icon: 'UsersIcon' as const,
    color: 'indigo' as const,
  },
]
/**
 * Role definitions with hierarchical permissions
 * Supports AgileBrain's multi-tenant access control model
 */
export const ROLES: RoleDefinition[] = [
  {
    id: 'role-agilebrain-admin',
    name: 'agilebrain-admin',
    displayName: 'AgileBrain Administrator',
    permissions: [
      { action: 'create-exercises', allowed: true, scope: 'global' },
      { action: 'assign-exercises', allowed: true, scope: 'global' },
      { action: 'view-results', allowed: true, scope: 'global' },
      { action: 'edit-participants', allowed: true, scope: 'global' },
      { action: 'manage-hierarchy', allowed: true, scope: 'global' },
      { action: 'view-analytics', allowed: true, scope: 'global' },
      { action: 'manage-integrations', allowed: true, scope: 'global' },
    ],
    hierarchyLevel: 'global',
  },
  {
    id: 'role-client-admin',
    name: 'client-admin',
    displayName: 'Client Administrator',
    permissions: [
      { action: 'create-exercises', allowed: false },
      { action: 'assign-exercises', allowed: true, scope: 'organization' },
      { action: 'view-results', allowed: true, scope: 'organization' },
      { action: 'edit-participants', allowed: true, scope: 'organization' },
      { action: 'manage-hierarchy', allowed: true, scope: 'organization' },
      { action: 'view-analytics', allowed: true, scope: 'organization' },
      { action: 'manage-integrations', allowed: true, scope: 'organization' },
    ],
    hierarchyLevel: 'organization',
  },
  {
    id: 'role-counselor',
    name: 'counselor',
    displayName: 'School Counselor',
    permissions: [
      { action: 'create-exercises', allowed: false },
      { action: 'assign-exercises', allowed: true, scope: 'school' },
      { action: 'view-results', allowed: true, scope: 'school' },
      { action: 'edit-participants', allowed: false },
      { action: 'manage-hierarchy', allowed: false },
      { action: 'view-analytics', allowed: true, scope: 'school' },
      { action: 'manage-integrations', allowed: false },
    ],
    hierarchyLevel: 'school',
  },
  {
    id: 'role-parent',
    name: 'parent',
    displayName: 'Parent/Guardian',
    permissions: [
      { action: 'create-exercises', allowed: false },
      { action: 'assign-exercises', allowed: false },
      { action: 'view-results', allowed: true, scope: 'cohort' },
      { action: 'edit-participants', allowed: false },
      { action: 'manage-hierarchy', allowed: false },
      { action: 'view-analytics', allowed: false },
      { action: 'manage-integrations', allowed: false },
    ],
    hierarchyLevel: 'cohort',
  },
]
/**
 * Generate comprehensive LetGrow hierarchy with 143 schools
 * Each school has grade-bands and role-groups
 * Supports efficacy tracking for donor/grant reporting
 */
export const generateLetGrowHierarchy = (): OrganizationHierarchy => {
  const schools: HierarchyNode[] = []
  // Generate 143 schools across different states
  const states = ['CA', 'NY', 'TX', 'FL', 'IL', 'PA', 'OH', 'GA', 'NC', 'MI']
  const schoolTypes = [
    'Elementary',
    'Middle School',
    'High School',
    'K-12 Academy',
  ]
  for (let i = 0; i < 143; i++) {
    const state = states[i % states.length]
    const schoolType = schoolTypes[i % schoolTypes.length]
    const schoolNumber = Math.floor(i / states.length) + 1
    const gradeBands: HierarchyNode[] = [
      {
        id: `node-lg-school-${i}-k2`,
        name: 'Grades K-2',
        type: 'grade-band',
        parentId: `node-lg-school-${i}`,
        metadata: {
          participantCount: 45 + Math.floor(Math.random() * 30),
          gradeLevels: ['K', '1', '2'],
        },
        children: [
          {
            id: `node-lg-school-${i}-k2-students`,
            name: 'Students',
            type: 'cohort',
            parentId: `node-lg-school-${i}-k2`,
            metadata: {
              participantCount: 45 + Math.floor(Math.random() * 30),
              roles: ['student'],
            },
            children: [],
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-01T00:00:00Z',
          },
        ],
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      },
      {
        id: `node-lg-school-${i}-35`,
        name: 'Grades 3-5',
        type: 'grade-band',
        parentId: `node-lg-school-${i}`,
        metadata: {
          participantCount: 50 + Math.floor(Math.random() * 35),
          gradeLevels: ['3', '4', '5'],
        },
        children: [
          {
            id: `node-lg-school-${i}-35-students`,
            name: 'Students',
            type: 'cohort',
            parentId: `node-lg-school-${i}-35`,
            metadata: {
              participantCount: 50 + Math.floor(Math.random() * 35),
              roles: ['student'],
            },
            children: [],
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-01T00:00:00Z',
          },
        ],
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      },
      {
        id: `node-lg-school-${i}-68`,
        name: 'Grades 6-8',
        type: 'grade-band',
        parentId: `node-lg-school-${i}`,
        metadata: {
          participantCount: 48 + Math.floor(Math.random() * 32),
          gradeLevels: ['6', '7', '8'],
        },
        children: [
          {
            id: `node-lg-school-${i}-68-students`,
            name: 'Students',
            type: 'cohort',
            parentId: `node-lg-school-${i}-68`,
            metadata: {
              participantCount: 48 + Math.floor(Math.random() * 32),
              roles: ['student'],
            },
            children: [],
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-01T00:00:00Z',
          },
        ],
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      },
      {
        id: `node-lg-school-${i}-912`,
        name: 'Grades 9-12',
        type: 'grade-band',
        parentId: `node-lg-school-${i}`,
        metadata: {
          participantCount: 42 + Math.floor(Math.random() * 28),
          gradeLevels: ['9', '10', '11', '12'],
        },
        children: [
          {
            id: `node-lg-school-${i}-912-students`,
            name: 'Students',
            type: 'cohort',
            parentId: `node-lg-school-${i}-912`,
            metadata: {
              participantCount: 42 + Math.floor(Math.random() * 28),
              roles: ['student'],
            },
            children: [],
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-01T00:00:00Z',
          },
        ],
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      },
    ]
    const roleGroups: HierarchyNode[] = [
      {
        id: `node-lg-school-${i}-parents`,
        name: 'Parents & Guardians',
        type: 'role-group',
        parentId: `node-lg-school-${i}`,
        metadata: {
          participantCount: Math.floor(
            (gradeBands.reduce(
              (sum, gb) => sum + (gb.metadata?.participantCount || 0),
              0,
            ),
            0) * 1.8,
          ),
          roles: ['parent', 'guardian'],
        },
        children: [],
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      },
      {
        id: `node-lg-school-${i}-teachers`,
        name: 'Teachers & Staff',
        type: 'role-group',
        parentId: `node-lg-school-${i}`,
        metadata: {
          participantCount: 12 + Math.floor(Math.random() * 8),
          roles: ['teacher', 'staff'],
        },
        children: [],
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      },
    ]
    const totalParticipants = [...gradeBands, ...roleGroups].reduce(
      (sum, node) => sum + (node.metadata?.participantCount || 0),
      0,
    )
    schools.push({
      id: `node-lg-school-${i}`,
      name: `${schoolType} ${schoolNumber} (${state})`,
      type: 'school',
      parentId: 'node-org-letgrow',
      metadata: {
        participantCount: totalParticipants,
        description: `LetGrow partner school in ${state}`,
        state,
        schoolType,
      },
      children: [...gradeBands, ...roleGroups],
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
    })
  }
  const rootNode: HierarchyNode = {
    id: 'node-org-letgrow',
    name: 'LetGrow',
    type: 'organization',
    parentId: null,
    metadata: {
      participantCount: schools.reduce(
        (sum, school) => sum + (school.metadata?.participantCount || 0),
        0,
      ),
      description:
        'National nonprofit promoting childhood independence and resilience',
    },
    children: schools,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  }
  const flatMap: { [id: string]: HierarchyNode } = {}
  const buildFlatMap = (node: HierarchyNode) => {
    flatMap[node.id] = node
    safeForEach('mockData.node.children', node.children, (child) =>
      buildFlatMap(child),
    )
  }
  buildFlatMap(rootNode)
  return {
    organizationId: 'org-letgrow',
    rootNode,
    flatMap,
  }
}
export const LETGROW_HIERARCHY = generateLetGrowHierarchy()
/**
 * Generate hierarchy for Kyiv Mental Guard (Ukrainian schools)
 * Simpler structure for demonstration
 */
export const generateKMGHierarchy = (): OrganizationHierarchy => {
  console.log('[mockData] Generating KMG hierarchy...')
  // CRITICAL: Validate UKRAINIAN_COHORTS exists and is an array
  if (!Array.isArray(UKRAINIAN_COHORTS)) {
    console.error(
      '[mockData] ❌ FATAL: UKRAINIAN_COHORTS is not an array in generateKMGHierarchy!',
    )
    console.error('[mockData] Type:', typeof UKRAINIAN_COHORTS)
    console.error('[mockData] Value:', UKRAINIAN_COHORTS)
    // Return empty hierarchy rather than crashing
    return {
      organizationId: 'org-kmg',
      rootNode: {
        id: 'node-org-kmg',
        name: 'Kyiv Mental Guard',
        type: 'organization',
        parentId: null,
        metadata: {
          participantCount: 0,
          description:
            'Mental health support for Ukrainian children (ERROR: No cohorts)',
        },
        children: [],
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      },
      flatMap: {},
    }
  }
  console.log(
    '[mockData] UKRAINIAN_COHORTS available:',
    UKRAINIAN_COHORTS.length,
  )
  // Create school nodes from first 10 Ukrainian schools for demo
  const schools: HierarchyNode[] = UKRAINIAN_SCHOOLS.slice(0, 10).map(
    (school) => {
      // Create cohorts for each school
      const cohorts: HierarchyNode[] = UKRAINIAN_COHORTS.filter(
        (c) => c.schoolId === school.id,
      ).map((cohort) => ({
        id: cohort.id,
        name: cohort.name,
        type: 'cohort' as const,
        parentId: school.id,
        metadata: {
          participantCount: cohort.participantCount,
        },
        children: [],
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      }))
      return {
        id: school.id,
        name: school.name,
        type: 'school' as const,
        parentId: 'node-org-kmg',
        metadata: {
          participantCount: cohorts.reduce(
            (sum, c) => sum + (c.metadata?.participantCount || 0),
            0,
          ),
          city: school.city,
        },
        children: cohorts,
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
      }
    },
  )
  const rootNode: HierarchyNode = {
    id: 'node-org-kmg',
    name: 'Kyiv Mental Guard',
    type: 'organization',
    parentId: null,
    metadata: {
      participantCount: schools.reduce(
        (sum, school) => sum + (school.metadata?.participantCount || 0),
        0,
      ),
      description: 'Mental health support for Ukrainian children',
    },
    children: schools,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  }
  const flatMap: { [id: string]: HierarchyNode } = {}
  const buildFlatMap = (node: HierarchyNode) => {
    flatMap[node.id] = node
    safeForEach('mockData.node.children', node.children, (child) =>
      buildFlatMap(child),
    )
  }
  buildFlatMap(rootNode)
  console.log(
    '[mockData] ✅ KMG hierarchy generated with',
    schools.length,
    'schools',
  )
  return {
    organizationId: 'org-kmg',
    rootNode,
    flatMap,
  }
}
export const MOCK_HIERARCHY = generateKMGHierarchy()
/**
 * Generate 6 unique exercises per LetGrow school
 * Variations by grade-band and role
 */
export const generateLetGrowExercises = (): ExerciseDefinition[] => {
  const exercises: ExerciseDefinition[] = []
  const exerciseTemplates = [
    {
      name: 'Independence & Self-Reliance',
      domains: ['Self', 'Material'],
      priming: {
        A: 'When I think about doing things on my own, I feel...',
        B: 'When I face challenges independently, I wish I felt less...',
      },
    },
    {
      name: 'Resilience & Coping Skills',
      domains: ['Self', 'Social'],
      priming: {
        A: 'When things get difficult, I feel...',
        B: 'When facing setbacks, I wish I felt less...',
      },
    },
    {
      name: 'Confidence & Self-Esteem',
      domains: ['Self', 'Spiritual'],
      priming: {
        A: 'When I think about my abilities, I feel...',
        B: 'When evaluating myself, I wish I felt less...',
      },
    },
    {
      name: 'Social Connection & Belonging',
      domains: ['Social'],
      priming: {
        A: 'When I am with friends and family, I feel...',
        B: 'In social situations, I wish I felt less...',
      },
    },
    {
      name: 'Problem-Solving & Decision-Making',
      domains: ['Material', 'Spiritual'],
      priming: {
        A: 'When I need to solve problems, I feel...',
        B: 'When making decisions, I wish I felt less...',
      },
    },
    {
      name: 'Growth Mindset & Learning',
      domains: ['Self', 'Material', 'Social'],
      priming: {
        A: 'When I learn something new, I feel...',
        B: 'When learning gets hard, I wish I felt less...',
      },
    },
  ]
  // Generate exercises for first 10 schools as examples
  for (let i = 0; i < 10; i++) {
    exerciseTemplates.forEach((template, idx) => {
      exercises.push({
        exerciseId: `ex-letgrow-school-${i}-${idx}`,
        title: {
          en: `${template.name} - School ${i + 1}`,
        },
        sets: {
          A: 'emotional-faces-set-a',
          B: 'emotional-faces-set-b',
        },
        useSuperset: false,
        timing: {
          perImageMs: 1800,
          interStimulusMs: 500,
          maxSetSeconds: 180,
        },
        primingCopy: {
          A: {
            en: template.priming.A,
          },
          B: {
            en: template.priming.B,
          },
        },
        algorithmVersion: 'ALG-1.2.0',
        interpretationTemplate: 'TPL-LETGROW-1.0.0',
        locales: ['en'],
      })
    })
  }
  return exercises
}
export const LETGROW_EXERCISES_DETAILED = generateLetGrowExercises()
/**
 * LetGrow efficacy metrics for donor/grant reporting
 * Tracks program impact across key resilience indicators
 */
export const LETGROW_EFFICACY_DETAILED: {
  programImpact: {
    [key: string]: EfficacyMetric
  }
  longitudinalData: {
    month: string
    independenceScore: number
    resilienceScore: number
    confidenceScore: number
    sampleSize: number
  }[]
} = {
  programImpact: {
    independence: {
      metricName: 'Independence Score',
      preScore: 62.3,
      postScore: 78.1,
      changePercent: 25.4,
      sampleSize: 12456,
      measurementDate: '2024-12-15',
    },
    resilience: {
      metricName: 'Resilience Index',
      preScore: 58.7,
      postScore: 73.2,
      changePercent: 24.7,
      sampleSize: 12456,
      measurementDate: '2024-12-15',
    },
    confidence: {
      metricName: 'Self-Confidence',
      preScore: 64.1,
      postScore: 79.8,
      changePercent: 24.5,
      sampleSize: 12456,
      measurementDate: '2024-12-15',
    },
    problemSolving: {
      metricName: 'Problem-Solving Skills',
      preScore: 60.4,
      postScore: 76.9,
      changePercent: 27.3,
      sampleSize: 12456,
      measurementDate: '2024-12-15',
    },
  },
  longitudinalData: [
    {
      month: 'Jan 2024',
      independenceScore: 62.3,
      resilienceScore: 58.7,
      confidenceScore: 64.1,
      sampleSize: 11234,
    },
    {
      month: 'Mar 2024',
      independenceScore: 66.8,
      resilienceScore: 62.3,
      confidenceScore: 68.5,
      sampleSize: 11567,
    },
    {
      month: 'May 2024',
      independenceScore: 70.2,
      resilienceScore: 66.1,
      confidenceScore: 72.3,
      sampleSize: 11892,
    },
    {
      month: 'Jul 2024',
      independenceScore: 73.5,
      resilienceScore: 68.9,
      confidenceScore: 75.1,
      sampleSize: 12145,
    },
    {
      month: 'Sep 2024',
      independenceScore: 75.9,
      resilienceScore: 71.4,
      confidenceScore: 77.6,
      sampleSize: 12334,
    },
    {
      month: 'Nov 2024',
      independenceScore: 77.3,
      resilienceScore: 72.5,
      confidenceScore: 78.9,
      sampleSize: 12423,
    },
    {
      month: 'Dec 2024',
      independenceScore: 78.1,
      resilienceScore: 73.2,
      confidenceScore: 79.8,
      sampleSize: 12456,
    },
  ],
}
/**
 * Mock exercise assignments to hierarchy nodes
 * Demonstrates flexible exercise deployment model
 */
export const MOCK_EXERCISE_ASSIGNMENTS: ExerciseAssignment[] = [
  {
    id: 'assign-1',
    exerciseId: 'ex-letgrow-school-0-0',
    nodeId: 'node-lg-school-0-k2',
    assignedBy: 'ab_root',
    assignedAt: '2024-01-15T10:00:00Z',
    status: 'active',
  },
  {
    id: 'assign-2',
    exerciseId: 'ex-letgrow-school-0-1',
    nodeId: 'node-lg-school-0-35',
    assignedBy: 'ab_root',
    assignedAt: '2024-01-15T10:00:00Z',
    status: 'active',
  },
]
/**
 * Calculate weighted positivity index
 * Accounts for unequal selection counts in assessment
 * @param selections - Array of weighted selections with positivity scores
 * @returns Weighted positivity index (0-100)
 */
export const calculateWeightedPositivityIndex = (
  selections: WeightedSelection[],
): number => {
  if (selections.length === 0) return 0
  const totalWeight = selections.reduce((sum, sel) => sum + sel.weight, 0)
  if (totalWeight === 0) return 0
  const weightedSum = selections.reduce(
    (sum, sel) => sum + sel.positivity * sel.weight,
    0,
  )
  return Math.round((weightedSum / totalWeight) * 100) / 100
}
// Add webhook log mock data
export interface WebhookLog {
  id: string
  timestamp: string
  event: string
  status: 'success' | 'failed'
  chatbotId: string
  userId: string
}
export const MOCK_WEBHOOK_LOGS: WebhookLog[] = [
  {
    id: 'log-001',
    timestamp: '2025-01-15T14:23:22Z',
    event: 'exercise.completed',
    status: 'success',
    chatbotId: 'KS-BOT-KMG-01',
    userId: 'EXT-UKR-000123',
  },
  {
    id: 'log-002',
    timestamp: '2025-01-15T15:10:25Z',
    event: 'exercise.completed',
    status: 'success',
    chatbotId: 'KS-BOT-KMG-01',
    userId: 'EXT-UKR-000124',
  },
  {
    id: 'log-003',
    timestamp: '2025-01-16T09:45:14Z',
    event: 'exercise.completed',
    status: 'success',
    chatbotId: 'KS-BOT-KMG-01',
    userId: 'EXT-UKR-000125',
  },
]
// Mock Firebase user IDs for tracking
export const MOCK_FIREBASE_USERS = [
  'firebase-user-001',
  'firebase-user-002',
  'firebase-user-003',
  'firebase-user-004',
  'firebase-user-005',
]
// Generate mock user image exposure data
export const generateUserImageExposure = (): UserImageExposure[] => {
  const exposures: UserImageExposure[] = []
  const imageIds = MOCK_IMAGES.map((img) => img.id)
  // Generate exposure history for each user
  MOCK_FIREBASE_USERS.forEach((userId) => {
    // Each user has seen 20-40 images across different sessions
    const seenCount = 20 + Math.floor(Math.random() * 20)
    const seenImages = new Set<string>()
    for (let i = 0; i < seenCount; i++) {
      const imageId = imageIds[Math.floor(Math.random() * imageIds.length)]
      // Avoid duplicates for this user
      if (seenImages.has(imageId)) continue
      seenImages.add(imageId)
      const image = MOCK_IMAGES.find((img) => img.id === imageId)
      if (!image) continue
      exposures.push({
        userId,
        imageId,
        imageSetId:
          Math.random() > 0.5
            ? 'emotional-faces-set-a'
            : 'emotional-faces-set-b',
        exerciseId: 'EX-EMO-UK-2025-10-v1',
        sessionId: `session-${userId}-${i}`,
        timestamp: new Date(
          Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000,
        ).toISOString(),
        domain: image.domain,
        level: image.level,
      })
    }
  })
  return exposures
}
export const MOCK_USER_IMAGE_EXPOSURE = generateUserImageExposure()
// Helper function to get images seen by a user
export const getImagesSeenByUser = (
  userId: string,
  windowDays?: number,
): string[] => {
  let exposures = MOCK_USER_IMAGE_EXPOSURE.filter(
    (exp) => exp.userId === userId,
  )
  if (windowDays) {
    const cutoffDate = new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000)
    exposures = exposures.filter((exp) => new Date(exp.timestamp) > cutoffDate)
  }
  return Array.from(new Set(exposures.map((exp) => exp.imageId)))
}
// Helper function to get available images for a user (excluding seen images)
export const getAvailableImagesForUser = (
  userId: string,
  imageSet: ImageSet,
  allImages: ImageMetadata[],
): ImageMetadata[] => {
  if (!imageSet.noRepeatPolicy?.enabled) {
    return allImages
  }
  const seenImageIds = getImagesSeenByUser(
    userId,
    imageSet.noRepeatPolicy.windowDays,
  )
  return allImages.filter((img) => !seenImageIds.includes(img.id))
}
// Helper function to record image exposure
export const recordImageExposure = (
  userId: string,
  imageId: string,
  imageSetId: string,
  exerciseId: string,
  domain: string,
  level: string,
): void => {
  MOCK_USER_IMAGE_EXPOSURE.push({
    userId,
    imageId,
    imageSetId,
    exerciseId,
    sessionId: `session-${Date.now()}`,
    timestamp: new Date().toISOString(),
    domain,
    level,
  })
}
// Helper function to simulate exercise generation with no-repeat filtering
export const generateExerciseImagesForUser = (
  userId: string,
  imageSet: ImageSet,
  allImages: ImageMetadata[],
  targetCount: number = 36,
): {
  selectedImages: ImageMetadata[]
  availableCount: number
  seenCount: number
} => {
  // Get images the user hasn't seen recently
  const availableImages = getAvailableImagesForUser(userId, imageSet, allImages)
  // Select images up to target count
  const selectedImages = availableImages.slice(0, targetCount)
  // Record exposure for selected images
  selectedImages.forEach((image) => {
    recordImageExposure(
      userId,
      image.id,
      imageSet.id,
      'exercise-generated',
      image.domain,
      image.level,
    )
  })
  return {
    selectedImages,
    availableCount: availableImages.length,
    seenCount: allImages.length - availableImages.length,
  }
}
// Helper function to get usage statistics for an image set
export const getImageSetUsageStats = (
  imageSet: ImageSet,
  userId?: string,
): {
  totalImages: number
  seenImages: number
  availableImages: number
  percentageSeen: number
  recentExposures: UserImageExposure[]
} => {
  if (!userId) {
    return {
      totalImages: imageSet.images.length,
      seenImages: 0,
      availableImages: imageSet.images.length,
      percentageSeen: 0,
      recentExposures: [],
    }
  }
  const seenImageIds = getImagesSeenByUser(
    userId,
    imageSet.noRepeatPolicy?.windowDays,
  )
  const setImageIds = imageSet.images || []
  const seenInSet = setImageIds.filter((id) => seenImageIds.includes(id))
  const recentExposures = MOCK_USER_IMAGE_EXPOSURE.filter(
    (exp) =>
      exp.userId === userId &&
      exp.imageSetId === imageSet.id &&
      setImageIds.includes(exp.imageId),
  )
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )
    .slice(0, 10)
  return {
    totalImages: setImageIds.length,
    seenImages: seenInSet.length,
    availableImages: setImageIds.length - seenInSet.length,
    percentageSeen:
      setImageIds.length > 0
        ? Math.round((seenInSet.length / setImageIds.length) * 100)
        : 0,
    recentExposures,
  }
}
/**
 * Organization-specific data retrieval functions
 * These functions return the appropriate mock data based on organization ID
 */
export function getParticipantsForOrg(orgId: string) {
  console.log('[mockData] getParticipantsForOrg called with:', orgId)
  if (orgId === 'org-kmg') return UKRAINIAN_PARTICIPANTS
  if (orgId === 'org-letgrow') return LETGROW_PARTICIPANTS
  if (orgId === 'org-mvcc') return MVCC_PARTICIPANTS
  if (orgId === 'org-duquesne') return DUQUESNE_PARTICIPANTS
  if (orgId === 'org-uvm') return UVM_PARTICIPANTS
  if (orgId === 'org-suny-orange') return SUNY_ORANGE_PARTICIPANTS
  if (orgId === 'org-suny-westchester') return SUNY_WESTCHESTER_PARTICIPANTS
  if (orgId === 'org-fingerlakes') return FINGERLAKES_PARTICIPANTS
  if (orgId === 'org-dicks') return DICKS_PARTICIPANTS
  if (orgId === 'org-acme') return ACME_PARTICIPANTS
  // Default fallback
  return []
}
export function getSubOrgsForOrg(orgId: string) {
  console.log('[mockData] getSubOrgsForOrg called with:', orgId)
  if (orgId === 'org-kmg') return UKRAINIAN_SCHOOLS
  if (orgId === 'org-letgrow') return LETGROW_SCHOOLS
  if (orgId === 'org-duquesne') return DUQUESNE_DEPARTMENTS
  if (orgId === 'org-uvm') return UVM_DEPARTMENTS
  if (orgId === 'org-suny-orange') return SUNY_ORANGE_DEPARTMENTS
  if (orgId === 'org-suny-westchester') return SUNY_WESTCHESTER_DEPARTMENTS
  if (orgId === 'org-fingerlakes') return FINGERLAKES_DEPARTMENTS
  if (orgId === 'org-dicks') return DICKS_STORES
  if (orgId === 'org-acme') return ACME_DEPARTMENTS
  // Colleges don't have sub-orgs
  return []
}
export function getGroupsForOrg(orgId: string) {
  console.log('[mockData] getGroupsForOrg called with:', orgId)
  if (orgId === 'org-kmg') return UKRAINIAN_GROUPS
  if (orgId === 'org-letgrow') return LETGROW_GROUPS
  if (orgId === 'org-duquesne') return DUQUESNE_DEPARTMENTS
  if (orgId === 'org-uvm') return UVM_DEPARTMENTS
  if (orgId === 'org-suny-orange') return SUNY_ORANGE_DEPARTMENTS
  if (orgId === 'org-suny-westchester') return SUNY_WESTCHESTER_DEPARTMENTS
  if (orgId === 'org-fingerlakes') return FINGERLAKES_DEPARTMENTS
  if (orgId === 'org-mvcc') return MVCC_GROUPS
  if (orgId === 'org-dicks') return DICKS_GROUPS
  if (orgId === 'org-acme') return ACME_GROUPS
  return []
}
export function getSubGroupsForOrg(orgId: string) {
  console.log('[mockData] getSubGroupsForOrg called with:', orgId)
  if (orgId === 'org-kmg') return UKRAINIAN_SUBGROUPS
  if (orgId === 'org-letgrow') return LETGROW_SUBGROUPS
  if (orgId === 'org-duquesne') return []
  if (orgId === 'org-uvm') return []
  if (orgId === 'org-suny-orange') return []
  if (orgId === 'org-suny-westchester') return []
  if (orgId === 'org-fingerlakes') return []
  if (orgId === 'org-mvcc') return []
  if (orgId === 'org-dicks') return []
  if (orgId === 'org-acme') return []
  // Colleges and MVCC don't have sub-groups
  return []
}
// Legacy cohort functions for backward compatibility
export function getCohortsForOrg(orgId: string) {
  // Map groups to cohort format for backward compatibility
  const groups = getGroupsForOrg(orgId)
  return groups.map((g) => ({
    id: g.id,
    name: g.name,
    subOrgId: g.subOrgId,
    participantCount: g.participantCount,
    activeAssignments: 0,
    createdAt: g.createdAt,
  }))
}
// Default hierarchy labels for each org type
export const DEFAULT_HIERARCHY_LABELS: Record<OrgType, HierarchyLabelConfig> = {
  K12: {
    subOrganization: 'School',
    group: 'Grade',
    subGroup: 'Classroom',
  },
  College: {
    subOrganization: 'Department',
    group: 'Course',
    subGroup: 'Section',
  },
  Corporate: {
    subOrganization: 'Location',
    group: 'Department',
    subGroup: 'Team',
  },
  Nonprofit: {
    subOrganization: 'Chapter',
    group: 'Program',
    subGroup: 'Cohort',
  },
}
// Validation utility to ensure all cohort constants are defined
console.log('✅ Defined cohorts:', {
  DUQUESNE_COHORTS: DUQUESNE_COHORTS.length,
  UVM_COHORTS: UVM_COHORTS.length,
  MVCC_COHORTS: MVCC_COHORTS.length,
  SUNY_ORANGE_COHORTS: SUNY_ORANGE_COHORTS.length,
  SUNY_WESTCHESTER_COHORTS: SUNY_WESTCHESTER_COHORTS.length,
  FINGERLAKES_COHORTS: FINGERLAKES_COHORTS.length,
  LETGROW_COHORTS: LETGROW_COHORTS.length,
  UKRAINIAN_COHORTS: UKRAINIAN_COHORTS.length,
})
console.log('✅ Mock Data Loaded:', {
  totalCohorts: MOCK_COHORTS.length,
  ukrainianCohorts: UKRAINIAN_COHORTS.length,
  letGrowCohorts: LETGROW_COHORTS.length,
})
console.log('[mockData] === FINAL VALIDATION ===')
console.log('[mockData] All cohort arrays:', {
  DUQUESNE_COHORTS: Array.isArray(DUQUESNE_COHORTS)
    ? DUQUESNE_COHORTS.length
    : 'NOT AN ARRAY',
  UVM_COHORTS: Array.isArray(UVM_COHORTS) ? UVM_COHORTS.length : 'NOT AN ARRAY',
  MVCC_COHORTS: Array.isArray(MVCC_COHORTS)
    ? MVCC_COHORTS.length
    : 'NOT AN ARRAY',
  SUNY_ORANGE_COHORTS: Array.isArray(SUNY_ORANGE_COHORTS)
    ? SUNY_ORANGE_COHORTS.length
    : 'NOT AN ARRAY',
  SUNY_WESTCHESTER_COHORTS: Array.isArray(SUNY_WESTCHESTER_COHORTS)
    ? SUNY_WESTCHESTER_COHORTS.length
    : 'NOT AN ARRAY',
  FINGERLAKES_COHORTS: Array.isArray(FINGERLAKES_COHORTS)
    ? FINGERLAKES_COHORTS.length
    : 'NOT AN ARRAY',
  LETGROW_COHORTS: Array.isArray(LETGROW_COHORTS)
    ? LETGROW_COHORTS.length
    : 'NOT AN ARRAY',
  UKRAINIAN_COHORTS: Array.isArray(UKRAINIAN_COHORTS)
    ? UKRAINIAN_COHORTS.length
    : 'NOT AN ARRAY',
})
console.log('[mockData] ✅ All exports validated')
console.log('[mockData] === END VALIDATION ===')
